import { useI18n } from '@i18n/I18nProvider'
import { useEffect, useRef, useState } from 'react'
import { Photo } from './Photo'

// La foto que se ve con el carrusel desplazado `scrollLeft` px, si cada foto ocupa `width` px.
export const slideAt = (scrollLeft: number, width: number, count: number): number => {
	if (width <= 0 || count <= 0) return 0
	return Math.min(count - 1, Math.max(0, Math.round(scrollLeft / width)))
}

// Fotos que se pasan deslizando el dedo, con puntos debajo que dicen cuántas hay y cuál se ve
// (ficha de especie). Los puntos también se pueden tocar: cada uno es "Foto n de total".
export const PhotoCarousel = ({ photos, alt }: { photos: string[]; alt: string }) => {
	const { t } = useI18n()
	const track = useRef<HTMLDivElement>(null)
	const [current, setCurrent] = useState(0)

	// Si cambian las fotos (otra especie), se vuelve a la primera.
	// biome-ignore lint/correctness/useExhaustiveDependencies: se reinicia al cambiar la lista
	useEffect(() => {
		setCurrent(0)
		track.current?.scrollTo?.({ left: 0 })
	}, [photos.join('|')])

	if (photos.length === 0) return null
	if (photos.length === 1)
		return <Photo src={photos[0]} alt={alt} className="mb-4 aspect-[4/3] w-full rounded-3xl object-cover" />

	const goTo = (index: number) => {
		setCurrent(index)
		const el = track.current
		el?.scrollTo?.({ left: index * el.clientWidth, behavior: 'smooth' })
	}

	return (
		<section aria-roledescription="carrusel" aria-label={alt} className="mb-4">
			<div
				ref={track}
				data-testid="carousel-track"
				onScroll={(e) =>
					setCurrent(slideAt(e.currentTarget.scrollLeft, e.currentTarget.clientWidth, photos.length))
				}
				className="flex snap-x snap-mandatory overflow-x-auto rounded-3xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
			>
				{photos.map((photo, index) => (
					<Photo
						key={photo}
						src={photo}
						alt={
							index === 0 ? alt : `${alt} · ${t('places.photo', { n: index + 1, total: photos.length })}`
						}
						loading={index === 0 ? 'eager' : 'lazy'}
						className="aspect-[4/3] w-full shrink-0 snap-center object-cover"
					/>
				))}
			</div>
			<div className="flex justify-center">
				{photos.map((photo, index) => (
					<button
						key={photo}
						type="button"
						aria-label={t('places.photo', { n: index + 1, total: photos.length })}
						aria-current={index === current ? 'true' : undefined}
						onClick={() => goTo(index)}
						className="flex size-12 items-center justify-center"
					>
						<span
							className={`block rounded-full transition-all motion-reduce:transition-none ${
								index === current ? 'size-3 bg-forest-800' : 'size-2.5 bg-earth-300'
							}`}
						/>
					</button>
				))}
			</div>
		</section>
	)
}
